import * as Yup from 'yup';
import { differenceInYears } from 'date-fns';
import { isEqual } from 'lodash-es';
import { policyType as PolicyType, offerExcessUM } from '@ourbranch/lookups';

import {
  canAddUmbrella,
  canAddUmbrellaUMUIM,
  getUmbrellaBIMinimum,
  getUmbrellaRequirements
} from 'core/helpers/quoter.service';

export const umbrellaValidationSchema = ({ policy, savedSegment, autoPolicyDetails }) =>
  Yup.object().shape({
    includeUmbrella: Yup.boolean()
      .required()
      .test(
        'canAddUmbrella',
        'Umbrella cannot be added because requirements are not met: the customer must have an active auto policy, auto BI must be at least $500K per occurrence ($250K/$500K for split or $500K for CSL), homeowner\u2019s liability must be at least $300K, and there cannot be any excluded drivers or drivers with a major violation, unverifiable driving record, or at-fault accident that disqualifies them from umbrella.',
        (val, { options: { context }, createError }) => {
          if (val) {
            const {
              homeCoverage,
              condoCoverage,
              global,
              initialValues,
              hasActiveOrFutureAutoPolicy,
              umbrellaCoverage,
              estimateOnly
            } = context;
            const renewalVariables = policy.renewalVariables;
            const drivers = autoPolicyDetails?.drivers;
            const policyLimitBIPD = autoPolicyDetails?.autoCoverage?.policyLimitBIPD;
            // Condo policies carry both a real condoCoverage and a stale/default homeCoverage
            // scaffold. `homeCoverage || condoCoverage` short-circuits to the truthy home object
            // and reads its low default coverageX, wrongly rejecting umbrella on condos. Select
            // the coverage object by the policy's actual type (mirrors the initialCoverageX logic
            // below). https://github.com/gobranch/branch/issues/31428
            const { coverageX } = (policy.policyType === PolicyType.Home ? homeCoverage : condoCoverage) || {};

            // if umbrella was already on and no umbrella values have changed, and coverageX hasnt changed, skip validation
            const initialCoverageX =
              policy.policyType === PolicyType.Home
                ? initialValues?.homeCoverage?.coverageX
                : initialValues?.condoCoverage?.coverageX;
            if (
              initialValues.includeUmbrella &&
              isEqual(umbrellaCoverage, initialValues.umbrellaCoverage) &&
              initialCoverageX === coverageX
            ) {
              return true;
            }

            // an estimate-only preview (#34681) skips the auto-side requirements; home liability still applies
            if (estimateOnly) {
              return (
                renewalVariables?.allowUmbrella ||
                getUmbrellaRequirements({ coverageX }).homeownersLiability ||
                createError({
                  message: 'Umbrella can’t be estimated because homeowner’s liability must be at least $300K.'
                })
              );
            }

            // if turning on for the first time, check if we have an active or future auto policy
            // (renewalVariables.allowUmbrella overrides, matching canAddUmbrella and the toggle gate)
            if (!initialValues.includeUmbrella && !hasActiveOrFutureAutoPolicy && !renewalVariables?.allowUmbrella) {
              return createError({
                message: 'Umbrella cannot be added because there is no active or future auto policy'
              });
            }

            if (
              !canAddUmbrella({ policyLimitBIPD, coverageX, drivers, global, renewalVariables, includeUmbrella: true })
            ) {
              const biMinimum = getUmbrellaBIMinimum(renewalVariables);
              const biCopy =
                biMinimum === 500
                  ? 'auto BI must be at least $500K per occurrence ($250K/$500K for split or $500K for CSL)'
                  : `auto BI must be at least $${biMinimum}K per occurrence (override applied to this policy)`;
              return createError({
                message: `Umbrella cannot be added because requirements are not met: the customer must have an active auto policy, ${biCopy}, homeowner’s liability must be at least $300K, and there cannot be any excluded drivers or drivers with a major violation, unverifiable driving record, or at-fault accident that disqualifies them from umbrella.`
              });
            }
            return true;
          }
          return true;
        }
      )
      .nullable(),
    umbrellaCoverage: Yup.mixed()
      .when('includeUmbrella', {
        is: true,
        then: Yup.object().shape({
          liabilityCoverageLimit: Yup.number()
            .test(
              'umbrella-limit-caped-at-2mil',
              'You cannot increase coverage over $2m',
              (value, { options: { context } }) => {
                // currently not allowing umbrella higher than 2mil
                const liabilityCoverageLimitChanged =
                  context.includeUmbrella && savedSegment?.umbrellaCoverage?.liabilityCoverageLimit !== value;
                if (liabilityCoverageLimitChanged && value > 2000000) {
                  return false;
                }
                return true;
              }
            )
            .required('Umbrella Limit is required')
            .nullable(),
          watercraftHullLengths: Yup.array()
            .of(
              Yup.number().test(
                'craftHullLengthsMax',
                'We do not allow boats with hull lengths over 26 feet.',
                (value, { options: { context } }) => {
                  const { includeUmbrella } = context;
                  return includeUmbrella ? value <= 26 : true;
                }
              )
            )
            .nullable(),
          uninsuredMotoristLimit: Yup.string()
            .test(
              'canHaveExcessUM',
              'You must have 500K per occurrence of UM/UIM to add excess UM to umbrella',
              (value, { options: { context } }) => {
                if (!value || value === '0') {
                  return true;
                }

                const { state, includeUmbrella, estimateOnly } = context;
                // an estimate-only preview (#34681) skips this auto-side requirement
                if (estimateOnly) {
                  return true;
                }
                const { policyLimitUMBI, policyLimitUIMBI } = autoPolicyDetails?.autoCoverage || {};

                const canAddUM = canAddUmbrellaUMUIM({ policyLimitUMBI, policyLimitUIMBI, state });

                return includeUmbrella ? canAddUM : true;
              }
            )
            .test(
              'stateCannotHaveExcessUM',
              'This state does not allow excess UM/UIM',
              (value, { options: { context } }) => {
                if (!value || value === '0') {
                  return true;
                }

                const { state, includeUmbrella } = context;

                return includeUmbrella ? offerExcessUM[state] : true;
              }
            )
            .test('requiredWhenOffering', 'Excess UM/UIM is required', (value, { options: { context } }) => {
              const { state } = context;
              return offerExcessUM[state] ? !!value : true;
            })
            .nullable(),
          numVehiclesTotal: Yup.number()
            .test(
              'minCount',
              'The total number of Umbrella vehicles cannot be less than the number of vehicles on the auto policy',
              (value, { options: { context } }) => {
                if (!autoPolicyDetails || !autoPolicyDetails?.cars?.length) {
                  return true;
                }
                const numCars = autoPolicyDetails?.cars?.length;
                const { includeUmbrella } = context;

                return includeUmbrella ? value >= numCars : true;
              }
            )
            .typeError('Number of vehicles is required')
            .required(),
          numLicensedDriversUnder25: Yup.number()
            .test(
              'minCountDrivers',
              'The total number of Umbrella drivers under age 25 cannot be less than the number of drivers under age 25 on the auto policy',
              (value, { options: { context } }) => {
                if (!autoPolicyDetails) {
                  return true;
                }
                // Age drivers as of the edited segment's effective date, not today. On a future-effective
                // term a driver who is 24 now but 25 at that date should not count as under-25. When the
                // segment date is somehow unknown, count every driver (stricter) rather than falling back
                // to today, which would silently reintroduce the wrong-reference-date bug.
                const referenceDate = savedSegment?.startDate ? new Date(savedSegment.startDate) : null;
                const driversUnder25 = referenceDate
                  ? autoPolicyDetails?.drivers.filter(
                      (d) => differenceInYears(referenceDate, new Date(d.dateOfBirth)) < 25
                    ).length
                  : autoPolicyDetails?.drivers.length;

                const { includeUmbrella } = context;
                return includeUmbrella ? value >= driversUnder25 : true;
              }
            )
            .typeError('Number of licensed drivers under 25 is required')
            .required(),
          numRVs: Yup.number()
            .required('Number of Golf Carts and Snowmobiles is required')
            .typeError('Number of Golf Carts and Snowmobiles is required'),
          numMotorcyclesScooters: Yup.number()
            .required('Number of Motorcycles/Scooters is required')
            .typeError('Number of Motorcycles/Scooters is required'),
          numATVs: Yup.number()
            .required('Number of ATVs is required')
            .default(null)
            .typeError('Number of ATVs is required'),
          numPersonalWatercraft: Yup.number()
            .required('Number of Personal Watercraft is required')
            .typeError('Number of Personal Watercraft is required')
        }),
        otherwise: Yup.mixed().nullable()
      })
      .nullable()
  });
